/**
 * Utility functions for formatting numbers and other data types
 */

/**
 * Formats a number as an integer (no decimals)
 * Converts numbers like 20.0 to 20
 * @param value - The number to format (can be number, string, null, or undefined)
 * @returns Formatted integer string or '-' if value is null/undefined
 */
export function formatInteger(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    return '-';
  }
  
  return Math.floor(num).toString();
}

/**
 * Converts a value to a number, returning as integer
 * @param value - The value to convert
 * @returns Integer number or 0 if conversion fails
 */
export function toInteger(value: number | string | null | undefined): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    return 0;
  }
  
  return Math.floor(num);
}

/**
 * Formats a number with proper decimal handling
 * For counts/rolls/bags: shows as integer
 * For weights/prices: shows with specified decimals
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 0 for integers)
 * @returns Formatted number string
 */
export function formatNumber(value: number | string | null | undefined, decimals: number = 0): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) {
    return '-';
  }
  
  if (decimals === 0) {
    return Math.floor(num).toString();
  }
  
  return num.toFixed(decimals);
}

/**
 * Truncates text to a maximum length and adds ellipsis
 * Useful for program names, lot numbers, and other long text in dropdowns
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation (default: 30)
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number = 30): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * CSS styles for text truncation with ellipsis
 * Use this with MUI sx prop for consistent text overflow handling
 * @param maxWidth - Maximum width for the text container (e.g., '150px', '100%')
 * @returns SX prop object for text truncation
 */
export const truncatedTextStyle = (maxWidth: string = '150px') => ({
  maxWidth,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
});

/**
 * Max widths for program name display in different contexts
 * Mobile: shorter to prevent overflow
 * Desktop: slightly longer for better readability
 */
export const PROGRAM_NAME_MAX_WIDTH = {
  mobile: '120px',
  desktop: '200px',
  chip: '150px',
  chipMobile: '100px',
  select: 35, // character limit for truncateText
};
